import React, { useEffect, useState } from "react";
import Select from "react-select";
import { connect, useSelector } from "react-redux";
import { fetchOperator, fetchRoute } from "containers/BuyOperatorToken/actions";
import "./index.css";
import { DatePicker, message } from "antd";
import { useTheme } from "context/themeContext";
import moment from "moment";
import ReactCountryFlag from "react-country-flag";
import { fetchCountries, fetchStates } from "./action";

// Dropdown option: flag + country name
const CountryOption = ({ innerProps, label, data }) => (
  <div
    {...innerProps}
    style={{ display: "flex", alignItems: "center", padding: "8px 12px", cursor: "pointer" }}
  >
    <ReactCountryFlag
      countryCode={data.countryCode}
      svg
      style={{ width: "20px", height: "15px", marginRight: "10px", borderRadius: "2px" }}
    />
    <span style={{ fontSize: "13px" }}>{label}</span>
  </div>
);

// Selected value: flag ONLY shown when closed
const CountrySingleValue = ({ data }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
    <ReactCountryFlag
      countryCode={data.countryCode}
      svg
      style={{ width: "24px", height: "18px", borderRadius: "2px", display: "block" }}
    />
  </div>
);

const DashSearch = ({ searchState, fetchRoute }) => {
  const [fromCountry, setFromCountry] = useState(null);
  const [fromState, setFromState] = useState(null);
  const [toCountry, setToCountry] = useState(null);
  const [toState, setToState] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);

  const [countries, setCountries] = useState([]);
  const [fromStates, setFromStates] = useState([]);
  const [toStates, setToStates] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingFromStates, setLoadingFromStates] = useState(false);
  const [loadingToStates, setLoadingToStates] = useState(false);

  const [transType, setTransType] = useState({ value: "INTERSTATE", label: "INTERSTATE" });
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);

  const routes = useSelector((state) => state.transportRoute || []);
  const { colorTheme, whiteLabelName, canReserve } = useTheme();

  useEffect(() => {
    const loadCountries = async () => {
      if (!canReserve) return;
      setLoadingCountries(true);
      const response = await fetchCountries(whiteLabelName);
      setCountries(
        response.data.map((c) => ({ value: c.id, label: c.name, countryCode: c.countryCode }))
      );
      setLoadingCountries(false);
    };
    loadCountries();
  }, [canReserve]);

  useEffect(() => {
    const load = async () => {
      if (fromCountry?.value) {
        setLoadingFromStates(true);
        const response = await fetchStates(fromCountry.value);
        setFromStates(response.data.map((s) => ({ value: s.name, label: s.name })));
        setLoadingFromStates(false);
        setFromState(null);
      } else {
        setFromStates([]);
        setFromState(null);
      }
    };
    if (canReserve) load();
  }, [fromCountry, canReserve]);

  useEffect(() => {
    const load = async () => {
      if (toCountry?.value) {
        setLoadingToStates(true);
        const response = await fetchStates(toCountry.value);
        setToStates(response.data.map((s) => ({ value: s.name, label: s.name })));
        setLoadingToStates(false);
        setToState(null);
      } else {
        setToStates([]);
        setToState(null);
      }
    };
    if (canReserve) load();
  }, [toCountry, canReserve]);

  useEffect(() => {
    if (whiteLabelName) fetchRoute(whiteLabelName);
  }, [whiteLabelName, fetchRoute]);

  useEffect(() => {
    let timer;
    if (loading) timer = setTimeout(() => setLoading(false), 1500);
    return () => { if (timer) clearTimeout(timer); };
  }, [loading]);

  const routeOptions =
    Array.isArray(routes?.data) && routes.data.length > 0
      ? routes.data.filter((item) => item?.name).map((item) => ({ value: item.name, label: item.name }))
      : [];

  const disabledDate = (current) => current && current < moment().startOf("day");

  const handleSearchClick = () => {
    if (canReserve) {
      if (!fromCountry?.value || !fromState?.value || !toCountry?.value || !toState?.value || !departureDate) {
        message.error("Please select origin country, origin state, destination country, destination state and date before searching.");
        return;
      }
    }
    setLoading(true);
    const data = canReserve
      ? {
          whitelabel: whiteLabelName,
          canReserve: true,
          origin: fromState?.value || "",
          destination: toState?.value || "",
          departureDate,
          time: new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
        }
      : {
          whitelabel: whiteLabelName,
          canReserve: false,
          routeType: transType.value,
          routeName: route?.value || "",
        };
    searchState(data);
  };

  const transportTypeOptions = [
    { value: "ALL", label: "ALL" },
    { value: "INTERSTATE", label: "INTERSTATE" },
    { value: "INTRASTATE", label: "INTRASTATE" },
  ];

  // Compact flag-only selector styles
  const countrySelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "44px",
      height: "44px",
      width: "50px",
      minWidth: "50px",
      border: "1px solid #e0e0e0",
      borderTopRightRadius: "0",
      borderBottomRightRadius: "0",
      borderRight: "none",
      borderRadius: "6px 0 0 6px",
      boxShadow: "none",
      cursor: "pointer",
      padding: "0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      flex: "unset",
      width: "50px",
    }),
    indicatorsContainer: () => ({ display: "none" }), // hide arrow
    placeholder: (provided) => ({ ...provided, fontSize: "18px", margin: "0" }),
    menu: (provided) => ({ ...provided, minWidth: "220px", zIndex: 1000 }),
    option: (provided) => ({ ...provided, padding: "0" }),
    singleValue: (provided) => ({
      ...provided,
      margin: "0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
  };

  // State selector styles — left border removed so it joins the flag box
  const stateSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "44px",
      height: "46px",
      minWidth: "160px",
      border: "1px solid #e0e0e0",
      borderRadius: "0",
      boxShadow: "none",
    }),
    placeholder: (provided) => ({ ...provided, fontSize: "14px", color: "#999" }),
    option: (provided) => ({ ...provided, fontSize: "13px" }),
  };

  return (
    <div className="dash_search_container">
      {canReserve ? (
        <>
          {/* ORIGIN */}
          <div className="inline-group">
            <span className="group-label">Origin</span>
            <div className="inline-group__controls">
              <Select
                styles={countrySelectStyles}
                onChange={setFromCountry}
                options={countries}
                value={fromCountry}
                placeholder="🌍"
                isLoading={loadingCountries}
                isDisabled={loadingCountries}
                components={{ Option: CountryOption, SingleValue: CountrySingleValue }}
              />
              <Select
                styles={stateSelectStyles}
                onChange={setFromState}
                options={fromStates}
                value={fromState}
                placeholder="Select state"
                isLoading={loadingFromStates}
                isDisabled={!fromCountry || loadingFromStates}
                noOptionsMessage={() => (!fromCountry ? "Select a country first" : "No states available")}
              />
            </div>
          </div>

          {/* DESTINATION */}
          <div className="inline-group">
            <span className="group-label">Destination</span>
            <div className="inline-group__controls">
              <Select
                styles={countrySelectStyles}
                onChange={setToCountry}
                options={countries}
                value={toCountry}
                placeholder="🌍"
                isLoading={loadingCountries}
                isDisabled={loadingCountries}
                components={{ Option: CountryOption, SingleValue: CountrySingleValue }}
              />
              <Select
                styles={stateSelectStyles}
                onChange={setToState}
                options={toStates}
                value={toState}
                placeholder="Select state"
                isLoading={loadingToStates}
                isDisabled={!toCountry || loadingToStates}
                noOptionsMessage={() => (!toCountry ? "Select a country first" : "No states available")}
              />
            </div>
          </div>

          {/* DATE */}
          <div className="inline-group">
            <span className="group-label">Travel Date</span>
            <DatePicker
              className="dash_search_date"
              onChange={(date, dateString) => setDepartureDate(dateString)}
              placeholder="Choose Date"
              disabledDate={disabledDate}
            />
          </div>

          {/* SEARCH */}
          <div className="inline-group btn-group">
            <span className="group-label">&nbsp;</span>
            <button
              onClick={handleSearchClick}
              className="dash_search_btn text-white"
              style={{ background: colorTheme }}
            >
              {loading ? "Fetching..." : "Search"}
            </button>
          </div>
        </>
      ) : (
        <>
          <Select
            className="dash_search_rou"
            onChange={setTransType}
            value={transType}
            options={transportTypeOptions}
          />
          <Select
            className="dash_search_ops"
            onChange={setRoute}
            options={routeOptions}
            value={route}
            placeholder={routeOptions.length > 0 ? "Select route" : "No routes available"}
          />
          <div className="submit_dash">
            <button
              onClick={handleSearchClick}
              className="dash_search_btn text-white"
              style={{ background: colorTheme }}
            >
              {loading ? "Fetching..." : "Search"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ common, selectOperatorState }) => ({
  loading: common.loading,
  selectOperatorState,
});

export default connect(mapStateToProps, { fetchOperator, fetchRoute })(DashSearch);